import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { SberatelskiObjekt } from '../types';
import ConfirmDialog from './ConfirmDialog';
import ObjektDialog from './ObjektDialog';

type SortField = 'id' | 'nazev' | 'typ_polozky' | 'sklad_id';
type SortDirection = 'asc' | 'desc' | null;

const KamenyPage: React.FC = () => {
  const [objekty, setObjekty] = useState<SberatelskiObjekt[]>([]);
  const [filteredObjekty, setFilteredObjekty] = useState<SberatelskiObjekt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [objektToDelete, setObjektToDelete] = useState<number | null>(null);
  const [objektDialogOpen, setObjektDialogOpen] = useState(false);
  const [editingObjektId, setEditingObjektId] = useState<number | undefined>(undefined);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Filtering state
  const [filters, setFilters] = useState({
    id: '',
    nazev: '',
    typ_polozky: '',
    sklad_id: '',
    vaha: '',
    rozmery: '',
    materialy: ''
  });
  const [showOnlyOrdered, setShowOnlyOrdered] = useState(false);
  const [showOnlySkryte, setShowOnlySkryte] = useState(false);

  const fetchKameny = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Načteme pouze objekty - API už vrací kompletní data včetně typů položek a materiálů
      const objektyIdsResponse = await apiClient.getObjekty(1, 1000, '', 1); // Sekce 1 = Minerály/Kameny
      
      if (objektyIdsResponse.objektIds && objektyIdsResponse.objektIds.length > 0) {
        const objektyResponse = await apiClient.getObjektyByIds(objektyIdsResponse.objektIds);
        setObjekty(objektyResponse.objekty || []);
        setFilteredObjekty(objektyResponse.objekty || []);
      } else {
        setObjekty([]);
        setFilteredObjekty([]);
      }
    } catch (err: any) {
      setError('Chyba při načítání kamenů: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    let direction: SortDirection = 'asc';
    
    if (sortField === field) {
      if (sortDirection === 'asc') {
        direction = 'desc';
      } else if (sortDirection === 'desc') {
        direction = null;
      }
    }
    
    setSortField(direction === null ? null : field);
    setSortDirection(direction);
  };

  useEffect(() => {
    let result = [...objekty];
    
    // Apply filters
    if (filters.id) {
      result = result.filter(obj => obj.id.toString().includes(filters.id));
    }
    if (filters.nazev) {
      result = result.filter(obj => 
        (obj.nazev || obj.jmeno || '').toLowerCase().includes(filters.nazev.toLowerCase())
      );
    }
    if (filters.typ_polozky) {
      result = result.filter(obj => 
        (obj.typ_polozky?.nazev || '').toLowerCase().includes(filters.typ_polozky.toLowerCase())
      );
    }
    if (filters.sklad_id) {
      result = result.filter(obj => 
        (obj.sklad_id || '').toLowerCase().includes(filters.sklad_id.toLowerCase())
      );
    }
    if (filters.vaha) {
      result = result.filter(obj => 
        (obj.vaha || '').toLowerCase().includes(filters.vaha.toLowerCase())
      );
    }
    if (filters.rozmery) {
      result = result.filter(obj => 
        (obj.rozmery || '').toLowerCase().includes(filters.rozmery.toLowerCase())
      );
    }
    if (filters.materialy) {
      result = result.filter(obj => 
        obj.materialy?.some(m => m.nazev.toLowerCase().includes(filters.materialy.toLowerCase()))
      );
    }
    
    // Apply checkbox filters
    if (showOnlyOrdered) {
      result = result.filter(obj => (obj.pocet_objednavek ?? 0) > 0);
    }
    if (showOnlySkryte) {
      result = result.filter(obj => obj.skryta === true || obj.skryta === 1);
    }
    
    // Apply sorting
    if (sortField && sortDirection) {
      result.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortField) {
          case 'id':
            aValue = a.id;
            bValue = b.id;
            break;
          case 'nazev':
            aValue = (a.nazev || a.jmeno || '').toLowerCase();
            bValue = (b.nazev || b.jmeno || '').toLowerCase();
            break;
          case 'typ_polozky':
            aValue = (a.typ_polozky?.nazev || '').toLowerCase();
            bValue = (b.typ_polozky?.nazev || '').toLowerCase();
            break;
          case 'sklad_id':
            aValue = (a.sklad_id || '').toLowerCase();
            bValue = (b.sklad_id || '').toLowerCase();
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredObjekty(result);
  }, [objekty, filters, sortField, sortDirection, showOnlyOrdered, showOnlySkryte]);

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = (id: number) => {
    setObjektToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingObjektId(id);
    setObjektDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingObjektId(undefined);
    setObjektDialogOpen(true);
  };

  const handleDialogClose = () => {
    setObjektDialogOpen(false);
    setEditingObjektId(undefined);
  };

  const handleDialogSuccess = () => {
    fetchKameny();
  };

  const confirmDelete = async () => {
    if (objektToDelete === null) return;

    try {
      await apiClient.deleteObjekt(objektToDelete);
      setDeleteDialogOpen(false);
      setObjektToDelete(null);
      // Znovu načteme data
      await fetchKameny();
    } catch (err: any) {
      setError('Chyba při mazání objektu: ' + (err.response?.data?.message || err.message));
      setDeleteDialogOpen(false);
      setObjektToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setObjektToDelete(null);
  };

  const handleToggleSkryta = async (id: number) => {
    try {
      const result = await apiClient.toggleSkryta(id);
      if (result.success) {
        // Znovu načteme data
        await fetchKameny();
      }
    } catch (err: any) {
      setError('Chyba při změně viditelnosti: ' + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => {
    fetchKameny();
  }, []);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return ' ⇅';
    if (sortDirection === 'asc') return ' ↑';
    if (sortDirection === 'desc') return ' ↓';
    return ' ⇅';
  };

  if (loading) return <div style={styles.loading}>Načítání...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <h2>Kameny (Minerály)</h2>
        <button style={styles.addButton} onClick={handleAdd}>
          + Přidat kámen
        </button>
      </div>
      
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('id')}>
              ID{getSortIcon('id')}
            </th>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('sklad_id')}>
              Skladove ID{getSortIcon('sklad_id')}
            </th>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('nazev')}>
              Název{getSortIcon('nazev')}
            </th>
            <th style={styles.th}>Váha</th>
            <th style={styles.th}>Rozměry</th>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('typ_polozky')}>
              Typ položky{getSortIcon('typ_polozky')}
            </th>
            <th style={styles.th}>Materiály</th>
            <th style={styles.th}>Média</th>
            <th style={styles.th}>Ks sklad</th>
            <th style={styles.th}>Akt objednávky</th>
            <th style={styles.th}>Akce</th>
          </tr>
          <tr>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.id}
                onChange={(e) => handleFilterChange('id', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.sklad_id}
                onChange={(e) => handleFilterChange('sklad_id', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.nazev}
                onChange={(e) => handleFilterChange('nazev', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.vaha}
                onChange={(e) => handleFilterChange('vaha', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.rozmery}
                onChange={(e) => handleFilterChange('rozmery', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.typ_polozky}
                onChange={(e) => handleFilterChange('typ_polozky', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}>
              <input
                type="text"
                value={filters.materialy}
                onChange={(e) => handleFilterChange('materialy', e.target.value)}
                placeholder="Filtr..."
                style={styles.filterInput}
              />
            </th>
            <th style={styles.filterCell}></th>
            <th style={styles.filterCell}></th>
            <th style={styles.filterCell}></th>
            <th style={styles.filterCell}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
                <label style={{ ...styles.checkboxLabel, fontSize: '12px', color: 'white' }}>
                  <input
                    type="checkbox"
                    checked={showOnlyOrdered}
                    onChange={(e) => setShowOnlyOrdered(e.target.checked)}
                  />
                  <span style={{ marginLeft: '5px' }}>S aktivními objednávkami</span>
                </label>
                <label style={{ ...styles.checkboxLabel, fontSize: '12px', color: 'white' }}>
                  <input
                    type="checkbox"
                    checked={showOnlySkryte}
                    onChange={(e) => setShowOnlySkryte(e.target.checked)}
                  />
                  <span style={{ marginLeft: '5px' }}>Skryté</span>
                </label>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredObjekty.length === 0 ? (
            <tr>
              <td colSpan={10} style={styles.noData}>Žádné kameny nenalezeny</td>
            </tr>
          ) : (
            filteredObjekty.map(objekt => {
              return (
                <tr key={objekt.id}>
                  <td style={styles.td}>{objekt.id}</td>
                  <td style={styles.td}>{objekt.sklad_id || '-'}</td>
                  <td style={styles.td}>{objekt.nazev || objekt.jmeno}</td>
                  <td style={styles.td}>{objekt.vaha || '-'}</td>
                  <td style={styles.td}>{objekt.rozmery || '-'}</td>
                  <td style={styles.td}>
                    {objekt.typ_polozky?.nazev || '-'}
                  </td>
                  <td style={styles.td}>
                    {objekt.materialy && objekt.materialy.length > 0
                      ? objekt.materialy.map(m => m.nazev).join(', ')
                      : '-'}
                  </td>
                  <td style={styles.td}>
                    {objekt.multimedia?.length || 0}
                  </td>
                  <td style={styles.td}>{objekt.pocet_ks_sklad ?? 1}</td>
                  <td style={styles.td}>
                    {objekt.pocet_objednavek ?? 0}
                    {objekt.objednavky && (objekt.objednavky.trim() !== '' && objekt.objednavky.trim() !== 'null') && (
                      <>
                        <br />
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>
                          ({objekt.objednavky})
                        </span>
                      </>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button
                        style={styles.editButton}
                        title="Editovat"
                        onClick={() => handleEdit(objekt.id)}
                      >
                        ✏️
                      </button>
                      <button
                        style={
                          objekt.skryta 
                            ? { ...styles.toggleButton, ...styles.toggleButtonHidden } 
                            : styles.toggleButton
                        }
                        title={objekt.skryta ? "Objekt je skrytý - kliknutím zobrazíte" : "Objekt je viditelný - kliknutím skryjete"}
                        onClick={() => handleToggleSkryta(objekt.id)}
                      >
                        {objekt.skryta ? (
                          <span style={{ position: 'relative', display: 'inline-block' }}>
                            👁️
                            <span style={{
                              position: 'absolute',
                              top: '50%',
                              left: '0',
                              right: '0',
                              height: '2px',
                              backgroundColor: '#dc3545',
                              transform: 'translateY(-50%) rotate(-45deg)'
                            }} />
                          </span>
                        ) : '👁️'}
                      </button>
                      <button
                        style={styles.deleteButton}
                        title="Smazat"
                        onClick={() => handleDelete(objekt.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Potvrzení smazání"
        message="Opravdu chcete smazat tento objekt? Tato akce je nevratná."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Smazat"
        cancelText="Zrušit"
      />

      <ObjektDialog
        isOpen={objektDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        objektId={editingObjektId}
        sekce={1}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px'
  },
  addButton: {
    padding: '10px 20px',
    fontSize: '16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold' as const
  },
  loading: {
    textAlign: 'center' as const,
    padding: '20px',
    fontSize: '16px',
  },
  error: {
    padding: '20px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    margin: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '20px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  th: {
    backgroundColor: '#343a40',
    color: 'white',
    padding: '12px',
    textAlign: 'left' as const,
    fontWeight: 'bold' as const,
    borderBottom: '2px solid #dee2e6',
  },
  filterCell: {
    backgroundColor: '#495057',
    padding: '8px',
    borderBottom: '2px solid #dee2e6',
  },
  filterInput: {
    width: '100%',
    padding: '4px 8px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
  },
  noData: {
    textAlign: 'center' as const,
    padding: '20px',
    color: '#6c757d',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
  deleteButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
  unreserveButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
  toggleButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'transform 0.2s',
  },
  toggleButtonHidden: {
  },
};

export default KamenyPage;
